/**
 * Singers Carousel Module
 * Handles the sliding carousel animation and interactions
 */

/**
 * Artist data
 */
interface Artist {
  img: string;
  name: string;
}

/**
 * Carousel instance state
 */
class CarouselInstance {
  container: HTMLElement;
  currentIndex: number;
  autoRotateInterval: number | null;
  isAnimating: boolean;
  listElement: HTMLElement;

  constructor(container: HTMLElement) {
    this.container = container;
    this.currentIndex = 5;
    this.autoRotateInterval = null;
    this.isAnimating = false;
    this.listElement = container.querySelector('.singers-list') as HTMLElement;
  }
}

// Artist data pool for dynamic content (uses AVIF with JPG fallback)
const ARTIST_POOL: Artist[] = [
  { img: 'abba', name: 'ABBA' },
  { img: 'adele', name: 'Adele' },
  { img: 'alicia_keys', name: 'Alicia Keys' },
  { img: 'amy', name: 'Amy Winehouse' },
  { img: 'aretha', name: 'Aretha Franklin' },
  { img: 'ariana_grande', name: 'Ariana Grande' },
  { img: 'backstreet_boys', name: 'Backstreet Boys' },
  { img: 'bastille', name: 'Bastille' },
  { img: 'beatles', name: 'The Beatles' },
  { img: 'bebe_rexha', name: 'Bebe Rexha' },
  { img: 'benson_boone', name: 'Benson Boone' },
  { img: 'beyonce', name: 'Beyoncé' },
  { img: 'bill_withers', name: 'Bill Withers' },
  { img: 'bob_marley', name: 'Bob Marley' },
  { img: 'brandon_lake', name: 'Brandon Lake' },
  { img: 'bruce_springsteen', name: 'Bruce Springsteen' },
  { img: 'bruno_mars', name: 'Bruno Mars' },
  { img: 'bts', name: 'BTS' },
  { img: 'carly', name: 'Carly Rae Jepsen' },
  { img: 'carrie_underwood', name: 'Carrie Underwood' },
  { img: 'celine_dion', name: 'Celine Dion' },
  { img: 'chris_brown', name: 'Chris Brown' },
  { img: 'chris_isaak', name: 'Chris Isaak' },
  { img: 'cold_play', name: 'Coldplay' },
  { img: 'cooper_and_gaga', name: 'Cooper & Gaga' },
  { img: 'cyndi', name: 'Cyndi Lauper' },
  { img: 'dua_lipa', name: 'Dua Lipa' },
  { img: 'ed_sheeran', name: 'Ed Sheeran' },
  { img: 'elvis_presley', name: 'Elvis Presley' },
  { img: 'eurythmics', name: 'Eurythmics' },
  { img: 'freddie_mercury', name: 'Freddie Mercury' },
  { img: 'gaynor', name: 'Gloria Gaynor' },
  { img: 'giveon', name: 'Giveon' },
  { img: 'harry_styles', name: 'Harry Styles' },
  { img: 'hozier', name: 'Hozier' },
  { img: 'imagine_dragons', name: 'Imagine Dragons' },
  { img: 'john_legend', name: 'John Legend' },
  { img: 'journey', name: 'Journey' },
  { img: 'justin_bieber', name: 'Justin Bieber' },
  { img: 'jvke', name: 'JVKE' },
  { img: 'kid_lario', name: 'Kid Laroi' },
  { img: 'kirk_franklin', name: 'Kirk Franklin' },
  { img: 'lady_gaga', name: 'Lady Gaga' },
  { img: 'laufey', name: 'Laufey' },
  { img: 'lauren_daigle', name: 'Lauren Daigle' },
  { img: 'louis_armstrong', name: 'Louis Armstrong' },
  { img: 'maneskin', name: 'Måneskin' },
  { img: 'michael_jackson', name: 'Michael Jackson' },
  { img: 'miley_cyrus', name: 'Miley Cyrus' },
  { img: 'nat_king_cole', name: 'Nat King Cole' },
  { img: 'nirvana', name: 'Nirvana' },
  { img: 'olivia_rodrigo', name: 'Olivia Rodrigo' },
  { img: 'one_direction', name: 'One Direction' },
  { img: 'one_republic', name: 'OneRepublic' },
  { img: 'paramore', name: 'Paramore' },
  { img: 'platters', name: 'The Platters' },
  { img: 'radiohead', name: 'Radiohead' },
  { img: 'rihanna', name: 'Rihanna' },
  { img: 'sabrina_carpenter', name: 'Sabrina Carpenter' },
  { img: 'shakira', name: 'Shakira' },
  { img: 'taylor_swift', name: 'Taylor Swift' },
  { img: 'thalia', name: 'Thalía' },
  { img: 'tori_kelly', name: 'Tori Kelly' },
  { img: 'usher', name: 'Usher' },
  { img: 'whitney', name: 'Whitney Houston' },
];

// Check if browser supports AVIF
let supportsAvif: boolean | null = null;
function checkAvifSupport(): Promise<boolean> {
  if (supportsAvif !== null) return Promise.resolve(supportsAvif);

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => { supportsAvif = true; resolve(true); };
    img.onerror = () => { supportsAvif = false; resolve(false); };
    // Tiny AVIF image
    img.src = 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKBzgABhAQ0AIy';
  });
}

// Get image URL with AVIF support
function getArtistImageUrl(imgBase: string): string {
  if (supportsAvif) {
    return `assets/images/artists/${imgBase}.avif`;
  }
  return `assets/images/artists/${imgBase}.jpg`;
}

// Initialize AVIF support check
checkAvifSupport();

/**
 * Singers Carousel Manager
 */
export class SingersCarousel {
  private carousels = new Map<string, CarouselInstance>();

  /**
   * Initialize carousels module
   */
  init(): void {
    // Module initialization - setup any necessary state
    // Actual rendering will be triggered by FlowManager when screens are shown
    console.log('SingersCarousel module initialized');
  }

  /**
   * Create slide element
   */
  private createSlideElement(artist: Artist): HTMLLIElement {
    const li = document.createElement('li');
    li.className = 'singer-slide new-next';
    li.style.backgroundImage = `url('${getArtistImageUrl(artist.img)}')`;
    li.innerHTML = `
            <div class="slide-overlay">
                <span class="singer-name">${artist.name}</span>
            </div>
        `;
    return li;
  }

  /**
   * Next slide
   */
  private next(instance: CarouselInstance): void {
    if (instance.isAnimating) return;
    instance.isAnimating = true;

    const container = instance.container;

    // Remove the hidden slide
    const hideSlide = container.querySelector('.singer-slide.hide');
    if (hideSlide) {
      hideSlide.remove();
    }

    // Move slides
    const prevSlide = container.querySelector('.singer-slide.prev');
    if (prevSlide) {
      prevSlide.classList.add('hide');
      prevSlide.classList.remove('prev');
    }

    const actSlide = container.querySelector('.singer-slide.act');
    if (actSlide) {
      actSlide.classList.add('prev');
      actSlide.classList.remove('act');
    }

    const nextSlide = container.querySelector('.singer-slide.next:not(.new-next)');
    if (nextSlide) {
      nextSlide.classList.add('act');
      nextSlide.classList.remove('next');
    }

    const newNextSlide = container.querySelector('.singer-slide.new-next');
    if (newNextSlide) {
      newNextSlide.classList.add('next');
      newNextSlide.classList.remove('new-next');
    }

    // Add new slide
    const nextArtist = ARTIST_POOL[instance.currentIndex % ARTIST_POOL.length];
    if (!nextArtist) return;
    const newSlide = this.createSlideElement(nextArtist);
    instance.listElement.appendChild(newSlide);

    instance.currentIndex++;

    // Force reflow to ensure transition works
    newSlide.offsetHeight;

    setTimeout(() => {
      instance.isAnimating = false;
    }, 1500);
  }

  /**
   * Previous slide
   */
  private prev(instance: CarouselInstance): void {
    if (instance.isAnimating) return;
    instance.isAnimating = true;

    const container = instance.container;

    // Remove the new-next slide
    const newNextSlide = container.querySelector('.singer-slide.new-next');
    if (newNextSlide) {
      newNextSlide.remove();
    }

    // Move slides
    const nextSlide = container.querySelector('.singer-slide.next');
    if (nextSlide) {
      nextSlide.classList.add('new-next');
    }

    const actSlide = container.querySelector('.singer-slide.act');
    if (actSlide) {
      actSlide.classList.add('next');
      actSlide.classList.remove('act');
    }

    const prevSlide = container.querySelector('.singer-slide.prev');
    if (prevSlide) {
      prevSlide.classList.add('act');
      prevSlide.classList.remove('prev');
    }

    const hideSlide = container.querySelector('.singer-slide.hide');
    if (hideSlide) {
      hideSlide.classList.add('prev');
      hideSlide.classList.remove('hide');
    }

    // Add new hide slide
    instance.currentIndex = instance.currentIndex - 2;
    if (instance.currentIndex < 0)
      instance.currentIndex = ARTIST_POOL.length + instance.currentIndex;

    const prevArtist =
      ARTIST_POOL[(instance.currentIndex - 4 + ARTIST_POOL.length) % ARTIST_POOL.length];
    if (!prevArtist) return;
    const newSlide = document.createElement('li');
    newSlide.className = 'singer-slide hide';
    newSlide.style.backgroundImage = `url('${getArtistImageUrl(prevArtist.img)}')`;
    newSlide.innerHTML = `
            <div class="slide-overlay">
                <span class="singer-name">${prevArtist.name}</span>
            </div>
        `;

    instance.listElement.insertBefore(newSlide, instance.listElement.firstChild);

    setTimeout(() => {
      instance.isAnimating = false;
    }, 500);
  }

  /**
   * Start auto-rotation
   */
  startAutoRotate(instance: CarouselInstance): void {
    instance.autoRotateInterval = window.setInterval(() => {
      this.next(instance);
    }, 600); // Rotate every 0.6 seconds (5x faster)
  }

  /**
   * Stop auto-rotation
   */
  stopAutoRotate(instance: CarouselInstance): void {
    if (instance.autoRotateInterval) {
      clearInterval(instance.autoRotateInterval);
      instance.autoRotateInterval = null;
    }
  }

  /**
   * Reset auto-rotation
   */
  private resetAutoRotate(instance: CarouselInstance): void {
    this.stopAutoRotate(instance);
    this.startAutoRotate(instance);
  }

  /**
   * Handle slide click
   */
  private handleSlideClick(instance: CarouselInstance, element: HTMLElement): void {
    if (element.classList.contains('next')) {
      this.next(instance);
      this.resetAutoRotate(instance);
    } else if (element.classList.contains('prev')) {
      this.prev(instance);
      this.resetAutoRotate(instance);
    }
  }

  /**
   * Render carousel
   */
  renderCarousel(containerId: string): CarouselInstance | null {
    const container = document.getElementById(containerId);
    if (!container) return null;

    // Check if carousel already exists in this container
    if (container.querySelector('.singers-carousel-section')) {
      console.log(`Carousel already rendered in ${containerId}`);
      return this.carousels.get(containerId) || null;
    }

    // Clone the template
    const template = document.getElementById('template-component-singers-carousel') as HTMLTemplateElement;
    if (!template) {
      console.error('Carousel template not found');
      return null;
    }

    const clone = template.content.cloneNode(true) as DocumentFragment;
    container.appendChild(clone);

    // Get the carousel section that was just added
    const carouselSection = container.querySelector(
      '.singers-carousel-section'
    ) as HTMLElement;
    const listElement = carouselSection.querySelector('.singers-list') as HTMLElement;

    // Ensure unique ID for this carousel
    const uniqueId = `singers-carousel-${containerId}`;
    listElement.id = uniqueId;

    // Initialize with 5 slides
    const initialArtists: Artist[] = [];
    for (let i = 0; i < 5; i++) {
      const artist = ARTIST_POOL[i % ARTIST_POOL.length];
      if (artist) {
        initialArtists.push(artist);
      }
    }

    // Create initial slides
    const classes = ['hide', 'prev', 'act', 'next', 'new-next'];
    initialArtists.forEach((artist, index) => {
      const li = document.createElement('li');
      li.className = `singer-slide ${classes[index]}`;
      li.style.backgroundImage = `url('${getArtistImageUrl(artist.img)}')`;
      li.innerHTML = `
                <div class="slide-overlay">
                    <span class="singer-name">${artist.name}</span>
                </div>
            `;
      listElement.appendChild(li);
    });

    // Create and store carousel instance
    const instance = new CarouselInstance(carouselSection);
    this.carousels.set(containerId, instance);

    // Add click handlers
    listElement.addEventListener('click', (event) => {
      const slide = (event.target as HTMLElement).closest('.singer-slide') as HTMLElement;
      if (slide) {
        this.handleSlideClick(instance, slide);
      }
    });

    // Pause on hover
    const carouselContainer = carouselSection.querySelector(
      '.carousel-container'
    ) as HTMLElement;
    if (carouselContainer) {
      carouselContainer.addEventListener('mouseenter', () => this.stopAutoRotate(instance));
      carouselContainer.addEventListener('mouseleave', () => this.startAutoRotate(instance));
    }

    // Start auto-rotation
    this.startAutoRotate(instance);

    return instance;
  }

  /**
   * Get carousels map
   */
  getCarousels(): Map<string, CarouselInstance> {
    return this.carousels;
  }
}

// Create singleton instance
export const singersCarousel = new SingersCarousel();
